import { ChangeSet, EditorSelection, type ChangeSpec, type EditorState, type SelectionRange, type TransactionSpec } from '@codemirror/state';
import { ensureSyntaxTree, syntaxTree } from '@codemirror/language';
import type { SyntaxNode, Tree } from '@lezer/common';

/**
 * Markdown formatting as transformations of readable source.
 *
 * Every command is a pure function from an EditorState to a transaction spec
 * (or null when it does not apply), so the primary pill, the selection pill and
 * keyboard shortcuts share exactly one behaviour, and a single undo reverts it.
 */

export type InlineFormat = 'bold' | 'italic' | 'strike' | 'code';
export type BlockStyle = 0 | 1 | 2 | 3;
export type ListKind = 'bullet' | 'ordered';

const INLINE: Record<InlineFormat, { node: string; mark: string }> = {
  bold: { node: 'StrongEmphasis', mark: '**' },
  italic: { node: 'Emphasis', mark: '*' },
  strike: { node: 'Strikethrough', mark: '~~' },
  code: { node: 'InlineCode', mark: '`' },
};

const WORD = /[\p{L}\p{N}_]/u;

function treeFor(state: EditorState, upTo: number): Tree {
  return ensureSyntaxTree(state, upTo, 100) ?? syntaxTree(state);
}

interface Span {
  from: number;
  to: number;
  openEnd: number;
  closeStart: number;
}

function spanOf(node: SyntaxNode): Span | null {
  const open = node.firstChild;
  const close = node.lastChild;
  if (!open || !close || open === close || !open.name.endsWith('Mark') || !close.name.endsWith('Mark')) return null;
  return { from: node.from, to: node.to, openEnd: open.to, closeStart: close.from };
}

/** The innermost node of `name` that contains the range. */
function enclosing(tree: Tree, from: number, to: number, name: string, collapsed: boolean): Span | null {
  for (const [pos, side] of [[from, 1], [to, -1]] as const) {
    for (let node: SyntaxNode | null = tree.resolveInner(pos, side); node; node = node.parent) {
      if (node.name !== name) continue;
      const span = spanOf(node);
      if (!span) continue;
      const inside = collapsed ? span.openEnd <= from && to <= span.closeStart : span.from <= from && to <= span.to;
      if (inside) return span;
    }
  }
  return null;
}

function containedSpans(tree: Tree, from: number, to: number, name: string): Span[] {
  const spans: Span[] = [];
  tree.iterate({
    from,
    to,
    enter(ref) {
      if (ref.name === name && ref.from >= from && ref.to <= to) {
        const span = spanOf(ref.node);
        if (span) spans.push(span);
        return false;
      }
    },
  });
  return spans;
}

function unwrapChanges(span: Span): ChangeSpec[] {
  return [
    { from: span.from, to: span.openEnd },
    { from: span.closeStart, to: span.to },
  ];
}

/** Toggle bold, italic, strikethrough or inline code on every selection range. */
export function toggleInline(state: EditorState, format: InlineFormat): TransactionSpec {
  const { node, mark } = INLINE[format];
  const tree = treeFor(state, state.selection.ranges.reduce((m, r) => Math.max(m, r.to), 0));
  const doc = state.doc;

  return state.changeByRange((range) => {
    const { from, to, empty } = range;

    // Inside an existing span: at its very end, step out; otherwise remove it.
    const current = enclosing(tree, from, to, node, empty);
    if (current) {
      if (empty && from === current.closeStart && current.closeStart > current.openEnd) {
        return { range: EditorSelection.cursor(current.to) };
      }
      const changes = ChangeSet.of(unwrapChanges(current), doc.length);
      return { changes, range: mapRange(range, changes) };
    }

    if (empty) {
      // An empty pair just typed: toggling again removes it.
      if (doc.sliceString(from - mark.length, from) === mark && doc.sliceString(from, from + mark.length) === mark) {
        const changes = ChangeSet.of({ from: from - mark.length, to: from + mark.length }, doc.length);
        return { changes, range: EditorSelection.cursor(from - mark.length) };
      }
      // Caret inside a word: format the word.
      const line = doc.lineAt(from);
      const before = line.text.slice(0, from - line.from);
      const after = line.text.slice(from - line.from);
      if (WORD.test(before.slice(-1)) && WORD.test(after.slice(0, 1))) {
        let start = from;
        let end = from;
        while (start > line.from && WORD.test(doc.sliceString(start - 1, start))) start--;
        while (end < line.to && WORD.test(doc.sliceString(end, end + 1))) end++;
        return wrapSegments(state, tree, [[start, end]], range, node, mark);
      }
      const changes = ChangeSet.of({ from, insert: mark + mark }, doc.length);
      return { changes, range: EditorSelection.cursor(from + mark.length) };
    }

    // Wrap each non-blank line of the selection separately: emphasis cannot cross paragraphs.
    const segments: Array<[number, number]> = [];
    for (let pos = from; pos <= to; ) {
      const line = doc.lineAt(pos);
      let start = Math.max(from, line.from);
      let end = Math.min(to, line.to);
      while (start < end && /\s/.test(doc.sliceString(start, start + 1))) start++;
      while (end > start && /\s/.test(doc.sliceString(end - 1, end))) end--;
      if (end > start) segments.push([start, end]);
      pos = line.to + 1;
    }
    if (!segments.length) return { range };

    // The selection is exactly one formatted span (markers included): remove it.
    if (segments.length === 1) {
      const [s, e] = segments[0];
      const exact = containedSpans(tree, s, e, node).find((sp) => sp.from === s && sp.to === e);
      if (exact) {
        const changes = ChangeSet.of(unwrapChanges(exact), doc.length);
        return { changes, range: EditorSelection.range(changes.mapPos(range.anchor, -1), changes.mapPos(range.head, -1)) };
      }
    }
    return wrapSegments(state, tree, segments, range, node, mark);
  });
}

function wrapSegments(
  state: EditorState,
  tree: Tree,
  segments: Array<[number, number]>,
  range: SelectionRange,
  node: string,
  mark: string,
) {
  const specs: ChangeSpec[] = [];
  for (const [s, e] of segments) {
    // Absorb the same format already inside the selection so it is not nested.
    for (const inner of containedSpans(tree, s, e, node)) specs.push(...unwrapChanges(inner));
    let open = mark;
    let close = mark;
    if (mark === '`' && state.doc.sliceString(s, e).includes('`')) {
      open = '`` ';
      close = ' ``';
    }
    specs.push({ from: s, insert: open }, { from: e, insert: close });
  }
  const changes = ChangeSet.of(specs, state.doc.length);
  const first = segments[0][0];
  const last = segments[segments.length - 1][1];
  if (range.empty) return { changes, range: EditorSelection.cursor(changes.mapPos(range.head, 1)) };
  const forward = range.head >= range.anchor;
  const start = changes.mapPos(first, 1);
  const end = changes.mapPos(last, -1);
  return { changes, range: forward ? EditorSelection.range(start, end) : EditorSelection.range(end, start) };
}

function mapRange(range: SelectionRange, changes: ChangeSet): SelectionRange {
  return EditorSelection.range(changes.mapPos(range.anchor, 1), changes.mapPos(range.head, 1));
}

// ---------------------------------------------------------------------------
// Line-level structure: headings, quotes, lists
// ---------------------------------------------------------------------------

export interface LinePrefix {
  /** Offset where block markers (list, heading) begin, after indentation and quote markers. */
  blockStart: number;
  quote: string;
  list: { kind: ListKind; length: number } | null;
  heading: { level: number; length: number } | null;
}

const PREFIX = /^([ \t]*)((?:>[ \t]?)*)([ \t]*)((?:[-*+]|\d{1,9}[.)])[ \t]+(?:\[[ xX]\][ \t]+)?)?(#{1,6}(?:[ \t]+|$))?/;

export function parseLinePrefix(text: string): LinePrefix {
  const m = PREFIX.exec(text)!;
  const [, indent, quote, inner, list, heading] = m;
  const blockStart = indent.length + quote.length + inner.length;
  return {
    blockStart,
    quote,
    list: list ? { kind: /^\d/.test(list) ? 'ordered' : 'bullet', length: list.length } : null,
    heading: heading ? { level: heading.trim().length, length: heading.length } : null,
  };
}

/** Line numbers touched by the selection; a range ending at a line start excludes that line. */
function selectedLines(state: EditorState): number[] {
  const lines = new Set<number>();
  for (const r of state.selection.ranges) {
    const first = state.doc.lineAt(r.from).number;
    let last = state.doc.lineAt(r.to).number;
    if (!r.empty && last > first && state.doc.line(last).from === r.to) last--;
    for (let n = first; n <= last; n++) lines.add(n);
  }
  return [...lines].sort((a, b) => a - b);
}

function linesToEdit(state: EditorState): Array<{ from: number; text: string; prefix: LinePrefix }> {
  const numbers = selectedLines(state);
  const all = numbers.map((n) => {
    const line = state.doc.line(n);
    return { from: line.from, text: line.text, prefix: parseLinePrefix(line.text) };
  });
  // A single line is always formatted (e.g. starting a list on an empty line); otherwise skip blanks.
  return all.length === 1 ? all : all.filter((l) => l.text.trim() !== '');
}

interface LineEdit {
  from: number;
  to?: number;
  insert?: string;
}

function finish(state: EditorState, specs: LineEdit[]): TransactionSpec | null {
  if (!specs.length) return null;
  const changes = ChangeSet.of(specs, state.doc.length);
  if (changes.empty) return null;
  // A position inside a replaced marker lands after its replacement, so the caret stays with the text.
  const map = (pos: number) => {
    for (const s of specs) {
      if (s.to !== undefined && s.to > s.from && pos >= s.from && pos <= s.to) return changes.mapPos(s.from, -1) + (s.insert?.length ?? 0);
    }
    return changes.mapPos(pos, 1);
  };
  const selection = EditorSelection.create(
    state.selection.ranges.map((r) => EditorSelection.range(map(r.anchor), map(r.head))),
    state.selection.mainIndex,
  );
  return { changes, selection, scrollIntoView: true };
}

/** Set the touched lines to a paragraph (0) or heading level; applying the current level returns to paragraph. */
export function setBlockStyle(state: EditorState, level: BlockStyle): TransactionSpec | null {
  const lines = linesToEdit(state);
  const target = lines.every((l) => (l.prefix.heading?.level ?? 0) === level) && level !== 0 ? 0 : level;
  const specs: LineEdit[] = [];
  for (const { from, prefix } of lines) {
    const start = from + prefix.blockStart;
    const end = start + (prefix.list?.length ?? 0) + (prefix.heading?.length ?? 0);
    const insert = target ? '#'.repeat(target) + ' ' : '';
    if (state.doc.sliceString(start, end) !== insert) specs.push({ from: start, to: end, insert });
  }
  return finish(state, specs);
}

export function toggleQuote(state: EditorState): TransactionSpec | null {
  const lines = linesToEdit(state);
  const content = lines.filter((l) => l.text.trim() !== '');
  const remove = (content.length ? content : lines).every((l) => l.prefix.quote !== '');
  const specs: LineEdit[] = [];
  for (const { from, text } of lines) {
    const indent = /^[ \t]*/.exec(text)![0].length;
    if (remove) {
      const m = /^>[ \t]?/.exec(text.slice(indent));
      if (m) specs.push({ from: from + indent, to: from + indent + m[0].length });
    } else {
      specs.push({ from: from + indent, insert: text.trim() === '' && lines.length > 1 ? '>' : '> ' });
    }
  }
  return finish(state, specs);
}

export function toggleList(state: EditorState, kind: ListKind): TransactionSpec | null {
  const lines = linesToEdit(state);
  const remove = lines.every((l) => l.prefix.list?.kind === kind);
  const specs: LineEdit[] = [];
  let n = 1;
  for (const { from, prefix } of lines) {
    const start = from + prefix.blockStart;
    const end = start + (prefix.list?.length ?? 0) + (prefix.heading?.length ?? 0);
    const heading = prefix.heading ? state.doc.sliceString(end - prefix.heading.length, end) : '';
    const marker = remove ? '' : kind === 'bullet' ? '- ' : `${n++}. `;
    specs.push({ from: start, to: end, insert: marker + heading });
  }
  return finish(state, specs);
}

// ---------------------------------------------------------------------------
// Links
// ---------------------------------------------------------------------------

export interface LinkInfo {
  from: number;
  to: number;
  label: string;
  url: string;
  urlFrom: number;
  urlTo: number;
  labelFrom: number;
  labelTo: number;
}

export function linkAt(state: EditorState): LinkInfo | null {
  const { from, to } = state.selection.main;
  const tree = treeFor(state, to);
  for (const [pos, side] of [[from, 1], [to, -1]] as const) {
    for (let node: SyntaxNode | null = tree.resolveInner(pos, side); node; node = node.parent) {
      if (node.name !== 'Link' || node.from > from || node.to < to) continue;
      const marks = node.getChildren('LinkMark');
      const urlNode = node.getChild('URL');
      if (marks.length < 2 || !urlNode) return null;
      return {
        from: node.from,
        to: node.to,
        labelFrom: marks[0].to,
        labelTo: marks[1].from,
        label: state.doc.sliceString(marks[0].to, marks[1].from),
        url: state.doc.sliceString(urlNode.from, urlNode.to).replace(/^<|>$/g, ''),
        urlFrom: urlNode.from,
        urlTo: urlNode.to,
      };
    }
  }
  return null;
}

/** Normalise what someone types into a link field. Returns '' for nothing usable. */
export function normaliseUrl(input: string): string {
  const url = input.trim();
  if (!url) return '';
  if (/^[a-z][a-z0-9+.-]*:/i.test(url) || url.startsWith('#') || url.startsWith('/') || url.startsWith('./') || url.startsWith('../')) return url;
  if (/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(url)) return `mailto:${url}`;
  if (/^[\w-]+(\.[\w-]+)+(:\d+)?([/?#].*)?$/.test(url)) return `https://${url}`;
  return url;
}

function destination(url: string): string {
  return /[\s()<>]/.test(url) ? `<${url.replace(/[<>]/g, encodeURIComponent)}>` : url;
}

/**
 * Create or edit a link at the main selection. An empty URL on an existing
 * link removes the link and keeps its text.
 */
export function applyLink(state: EditorState, rawUrl: string): TransactionSpec | null {
  const url = normaliseUrl(rawUrl);
  const existing = linkAt(state);
  if (existing) {
    if (!url) {
      return {
        changes: [
          { from: existing.from, to: existing.labelFrom },
          { from: existing.labelTo, to: existing.to },
        ],
        selection: EditorSelection.range(existing.from, existing.from + existing.label.length),
      };
    }
    return {
      changes: { from: existing.urlFrom, to: existing.urlTo, insert: destination(url) },
      selection: EditorSelection.cursor(existing.from + existing.label.length + 4 + destination(url).length),
    };
  }
  if (!url) return null;

  let { from, to } = state.selection.main;
  if (from === to) {
    const line = state.doc.lineAt(from);
    let start = from;
    let end = to;
    while (start > line.from && WORD.test(state.doc.sliceString(start - 1, start))) start--;
    while (end < line.to && WORD.test(state.doc.sliceString(end, end + 1))) end++;
    if (start < from && end > to) [from, to] = [start, end];
  }
  const label = state.doc.sliceString(from, to);
  const insert = `[${label}](${destination(url)})`;
  return {
    changes: { from, to, insert },
    // With no label yet, wait inside the brackets for one; otherwise continue after the link.
    selection: EditorSelection.cursor(label ? from + insert.length : from + 1),
    scrollIntoView: true,
  };
}

export function isProbablyUrl(text: string): boolean {
  return /^(https?:\/\/|mailto:)\S+$/i.test(text.trim());
}

// ---------------------------------------------------------------------------
// State for the controls
// ---------------------------------------------------------------------------

export interface FormatState {
  bold: boolean;
  italic: boolean;
  strike: boolean;
  code: boolean;
  link: boolean;
  block: number;
  list: ListKind | null;
  quote: boolean;
}

export const NO_FORMAT: FormatState = { bold: false, italic: false, strike: false, code: false, link: false, block: 0, list: null, quote: false };

export function formatState(state: EditorState): FormatState {
  const { from, to, empty } = state.selection.main;
  const tree = syntaxTree(state);
  const has = (f: InlineFormat) => {
    if (enclosing(tree, from, to, INLINE[f].node, empty)) return true;
    // A selection that exactly covers a formatted span also counts.
    return !empty && containedSpans(tree, from, to, INLINE[f].node).some((s) => s.from === from && s.to === to);
  };
  const prefix = parseLinePrefix(state.doc.lineAt(state.selection.main.head).text);
  return {
    bold: has('bold'),
    italic: has('italic'),
    strike: has('strike'),
    code: has('code'),
    link: linkAt(state) !== null,
    block: prefix.heading?.level ?? 0,
    list: prefix.list?.kind ?? null,
    quote: prefix.quote !== '',
  };
}

export function sameFormat(a: FormatState, b: FormatState): boolean {
  return (Object.keys(a) as (keyof FormatState)[]).every((k) => a[k] === b[k]);
}
